'use client'

import dynamic from 'next/dynamic'
import { FileText, Download, FolderIcon } from 'lucide-react'
import { getServerSideURL } from '@/utilities/getURL'
import { cn } from '@/utilities/cn'

const PdfFirstPagePreview = dynamic(
  () => import('@/components/pdf/PdfFirstPagePreview').then((m) => m.PdfFirstPagePreview),
  {
    ssr: false,
    loading: () => <div className="flex h-full w-full animate-pulse bg-muted/20" />,
  },
)

type PdfLike = {
  url?: string | null
  externalUrl?: string | null
  filename?: string | null
  title?: string | null
  filesize?: number | null
}

type DocumentsBlockProps = {
  blockType: 'courseDocuments'
  id?: string | null
  display?: 'list' | 'previews' | null
  columns?: '1' | '2' | null
  title?: string | null
  description?: string | null
  documents?: (PdfLike | string)[] | null
}

export const DocumentsBlockComponent: React.FC<DocumentsBlockProps> = ({
  display,
  columns,
  title,
  description,
  documents,
}) => {
  const docs = Array.isArray(documents)
    ? documents.filter((doc): doc is PdfLike => typeof doc === 'object' && doc !== null)
    : []

  if (!docs.length) return null

  const serverURL = getServerSideURL()
  const sectionTitle = title || 'Cursusmateriaal'
  const sectionDescription = description || 'Download werkbladen en checklists voor deze cursus.'
  const mode = display ?? 'list'
  const columnCount = columns === '2' ? 2 : 1

  return (
    <section className="space-y-6 rounded-2xl border border-border/60 bg-card/30 p-4 sm:p-8">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <FolderIcon className="h-6 w-6 text-primary" />
        </div>
        <div className="min-w-0 space-y-1">
          <h3 className="text-lg font-semibold text-foreground">{sectionTitle}</h3>
          <p className="text-sm text-muted-foreground">{sectionDescription}</p>
        </div>
      </div>

      {mode === 'previews' ? (
        <div className={cn('grid gap-6', columnCount === 2 ? 'md:grid-cols-2' : 'grid-cols-1')}>
          {docs.map((pdf, docIndex) => {
            // Use externalUrl (direct Vercel Blob) if available
            const href = pdf.externalUrl
              ? pdf.externalUrl
              : pdf.url
                ? `${serverURL}${pdf.url}`
                : pdf.filename
                  ? `${serverURL}/media/${pdf.filename}`
                  : null

            if (!href) {
              return (
                <div
                  className="rounded-xl border-2 border-dashed border-border/60 bg-muted/20 p-4 text-center text-sm text-muted-foreground"
                  key={`missing-${docIndex}`}
                >
                  Bestand ontbreekt. Upload de PDF in de Mediabibliotheek.
                </div>
              )
            }

            const fileSizeKB = pdf.filesize ? Math.round(pdf.filesize / 1024) : null
            const fileSizeDisplay = fileSizeKB
              ? fileSizeKB > 1024
                ? `${(fileSizeKB / 1024).toFixed(1)} MB`
                : `${fileSizeKB} KB`
              : null

            const docTitle = pdf.title || pdf.filename || 'Document'

            return (
              <article
                className="overflow-hidden rounded-2xl border border-border/60 bg-background/50 shadow-sm transition-colors hover:border-primary/30"
                key={pdf.filename || `doc-${docIndex}`}
              >
                <a
                  aria-label={`Open ${docTitle}`}
                  className="block"
                  href={href}
                  rel="noreferrer"
                  target="_blank"
                >
                  <div className="aspect-[3/4] w-full overflow-hidden bg-muted/10">
                    <PdfFirstPagePreview src={href} title={docTitle} />
                  </div>
                </a>

                <div className="flex items-center gap-4 border-t border-border/60 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <FileText aria-hidden className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-foreground">{docTitle}</p>
                    <p className="text-xs text-muted-foreground">
                      PDF Document{fileSizeDisplay ? ` • ${fileSizeDisplay}` : ''}
                    </p>
                  </div>
                  <a
                    className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                    href={href}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Open</span>
                  </a>
                </div>
              </article>
            )
          })}
        </div>
      ) : (
        <ul className="space-y-3">
          {docs.map((pdf, docIndex) => {
            // Use externalUrl (direct Vercel Blob) if available
            const href = pdf.externalUrl
              ? pdf.externalUrl
              : pdf.url
                ? `${serverURL}${pdf.url}`
                : pdf.filename
                  ? `${serverURL}/media/${pdf.filename}`
                  : null

            if (!href) {
              return (
                <li
                  className="rounded-xl border-2 border-dashed border-border/60 bg-muted/20 p-4 text-center text-sm text-muted-foreground"
                  key={`missing-${docIndex}`}
                >
                  Bestand ontbreekt. Upload de PDF in de Mediabibliotheek.
                </li>
              )
            }

            const fileSizeKB = pdf.filesize ? Math.round(pdf.filesize / 1024) : null
            const fileSizeDisplay = fileSizeKB
              ? fileSizeKB > 1024
                ? `${(fileSizeKB / 1024).toFixed(1)} MB`
                : `${fileSizeKB} KB`
              : null

            return (
              <li key={pdf.filename || `doc-${docIndex}`}>
                <a
                  className="group flex items-center gap-4 rounded-xl border border-border/60 bg-background/50 p-4 transition-all hover:border-primary/30 hover:bg-card/60 hover:shadow-sm"
                  href={href}
                  rel="noreferrer"
                  target="_blank"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <FileText aria-hidden className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {pdf.title || pdf.filename || 'Document'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF Document{fileSizeDisplay ? ` • ${fileSizeDisplay}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download</span>
                  </div>
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
